/**
 * AI judge result schemas — surfaced on every `ContentVersionResponse`
 * (issue #217). The persistence layer is owned by the AI judge runner
 * (`api/src/ai/judges/runner.ts`); these shapes are the wire DTOs the
 * admin SPA renders on `ContentItemDetailPage` so operators can see the
 * five quality bars at a glance.
 *
 * Five judges fire on every AI Studio Accept:
 *   ACCURACY · READABILITY · AI_SLOP · SEO · BRAND_TONE
 *
 * Status thresholds are owned by the runner (see
 * `api/src/ai/judges/types.ts` — score >=80 PASS, >=70 WARNING, <70 FAIL).
 * The wire shape carries the resolved status alongside the raw score so the
 * SPA can colour the chip without re-applying thresholds (and so a future
 * threshold tweak doesn't desync clients).
 *
 * Slice boundary:
 *   - This file is type-only at the SPA boundary — judges are read-only
 *     surface today. The Re-run button (issue #217 follow-up) will live
 *     in `ai/studio` or its own slice, not here.
 *   - `issuesJson` (the structured per-issue detail) is deliberately
 *     omitted from the DTO; the UI strip only needs `summary`. A follow-up
 *     can surface issues separately without changing this shape.
 */

import { z } from "zod";
import type { AiJudgeType, JudgeStatus } from "../types/index.js";

/**
 * Mirror of `AiJudgeType` in Prisma — the canonical 5-judge enum. The order
 * here matches `JUDGE_TYPES` in `api/src/ai/judges/types.ts`, which is the
 * order the runner uses + the order the SPA renders chips in.
 */
export const aiJudgeTypeSchema = z.enum([
  "ACCURACY",
  "READABILITY",
  "AI_SLOP",
  "SEO",
  "BRAND_TONE",
]) satisfies z.ZodType<AiJudgeType>;

/**
 * Mirror of `JudgeStatus` in Prisma — the three-bar pass/warn/fail signal
 * the SPA colours chips off. PASS → success, WARNING → warning, FAIL →
 * error.
 */
export const judgeStatusSchema = z.enum([
  "PASS",
  "WARNING",
  "FAIL",
]) satisfies z.ZodType<JudgeStatus>;

/**
 * Wire shape for a single `AiJudgeResult` row, scoped to what the
 * `ContentItemDetailPage` strip renders. The persisted row also carries
 * `aiGenerationId`, `issuesJson`, and a few audit fields the operator UI
 * doesn't need today; we keep them out of the DTO to minimise the wire
 * payload and the surface area a follow-up issue has to migrate.
 *
 * `judgedAt` is the ISO-string serialisation of the row's `createdAt`
 * column — semantically clearer at the boundary than re-using the
 * Prisma column name.
 *
 * `summary` is the human-readable rationale the chip tooltip surfaces.
 */
export const aiJudgeResultResponseSchema = z
  .object({
    id: z.string(),
    judgeType: aiJudgeTypeSchema,
    score: z.number().int().min(0).max(100),
    status: judgeStatusSchema,
    summary: z.string(),
    judgedAt: z.string(),
  })
  .strict();

export type AiJudgeResultResponse = z.infer<typeof aiJudgeResultResponseSchema>;

/**
 * Wire shape for `POST /api/admin/content-versions/:contentVersionId/judges/run`.
 *
 * Returns the freshly-persisted judge rows for the version so the SPA can
 * paint the strip with the new scores without a follow-up GET on the
 * parent ContentItem. The shape mirrors the `judges` field on
 * `ContentVersionResponse` so the SPA can swap the cached version's
 * `judges` array in place after a successful re-run.
 *
 * The endpoint replaces prior `AiJudgeResult` rows + `AI_JUDGE`-sourced
 * `ContentFlag` rows for the version atomically — the runner's idempotent
 * contract (see `api/src/ai/judges/runner.ts`). Manually-raised flags
 * (source: USER) survive.
 */
export const judgeRunResponseSchema = z
  .object({
    judges: z.array(aiJudgeResultResponseSchema),
  })
  .strict();

export type JudgeRunResponse = z.infer<typeof judgeRunResponseSchema>;
